import { useEffect, useState } from 'react';
import { Box, Divider, List } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { getCurrencyDetails, getLocaleLongDate } from '../../../utils/common';
import LocaleKeys from '../../../locales/keys';
import { makeStyles } from '@material-ui/core/styles';
import OrderHistoryListHeader from './OrderHistoryListHeader.jsx';
import ListItemComponent from '../../../common/listItem/ListItemComponent.jsx';
import { refundTaxHandler } from '../../helpers/refundTax';
import { getThumbnailUrl } from '../../../checkout/helpers/paymentOrderHelpers';

const useStyles = makeStyles((theme) => ({
  dividerBlack: {
    backgroundColor: `${theme.palette.custom.lightBlack}`,
    height: '1px',
    marginBottom: '5px',
  },
  orderList: {
    backgroundColor: theme.palette.custom.white,
    borderRadius: '10px',
    border: `solid 2px #d0d0d2`,
    marginBottom: '20px',
    padding: '20px',
    [theme.breakpoints.down('sm')]: {
      padding: '20px',
    },
  },
  orderListItem: {
    width: '100%',
    paddingTop: '0px',
    [theme.breakpoints.up('sm')]: {
      padding: '0 0px',
    },
  },
  orderListItemKey1: {
    padding: '0 15px',
    margin: 0,
  },
  orderListItemKey2: {
    [theme.breakpoints.down('sm')]: {
      maxWidth: '7.2rem',
    },
  },
  orderListItemKey3: {
    width: '50%',
    padding: '0 15px',
    margin: 0,
    alignSelf: 'center',
    [theme.breakpoints.down('md')]: {
      width: '50%',
    },
    [theme.breakpoints.only('xs')]: {
      width: '55%',
    },
  },
  orderListItemText: {
    padding: '0 15px',
    margin: 0,
    textAlign: 'end',
  },
  orderListHeader: {
    alignItems: 'flex-start',
    paddingTop: '0',
    paddingBottom: '0',
    marginTop: '15px',
    marginBottom: '3px',
  },
  avatarListItem: {
    [theme.breakpoints.up('md')]: {
      minWidth: '328px',
      padding: '0 15px',
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: '141.31px',
      padding: '0 15px',
    },
  },
  productLogo: {
    width: 'auto',
    height: 'auto',
    maxWidth: '220px',
    margin: '10px 0px 20px',
    [theme.breakpoints.only('xs')]: {
      maxWidth: '125px',
      height: '14px',
    },
    [theme.breakpoints.down('md')]: {
      width: '145px',
      height: '16px',
    },
  },
  f1DigitalManagement: {
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      whiteSpace: 'nowrap',
    },
  },
  invoiceAddress: {
    margin: 0,
    whiteSpace: 'pre-line',
    paddingBottom: '3px',
  },
  invoiceAddressListItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-Start',
    paddingLeft: '0',
    paddingTop: '15px',
    paddingBottom: 0,
  },
  taxInvoice: {
    width: '50%',
    padding: '0 15px',
    [theme.breakpoints.down('sm')]: {
      width: '50%',
    },
  },
  orderInvoiceAndNumber: {
    width: '50%',
    padding: '0 15px',
    [theme.breakpoints.between('xs', 'sm')]: {
      width: '50%',
      padding: '0 8px',
    },
    [theme.breakpoints.between('sm', 'md')]: {
      width: '50%',
      padding: '0 15px',
    },
  },
  taxItem: {
    paddingLeft: '2.5rem',
    paddingTop: '15px',
  },
  taxAmount: {
    [theme.breakpoints.up('md')]: {
      fontSize: '25px',
    },
    [theme.breakpoints.down('lg')]: {
      fontSize: '20px',
    },
  },
  totalAmount: {
    fontWeight: 700,
    textAlign: 'end',
    [theme.breakpoints.up('md')]: {
      fontSize: '25px',
    },
    [theme.breakpoints.down('lg')]: {
      fontSize: '20px',
    },
  },
  orderHistoryListItem: {
    paddingTop: '15px',
  },
  orderHistoryListLastItem: {
    paddingBottom: '3px',
  },
  orderListLastItemText: {
    paddingRight: 0,
  },
  titilliumSemiboldFont: {
    fontFamily: 'titillium-semibold',
    WebkitFontSmoothing: 'auto',
  },
}));

const OrderHistoryInvoice = ({
  retrieveOrder,
  showInvoice,
  productMetadata,
}) => {
  const { Completed, Currency, Ordered, OrderNumber, Totals, TypeName } =
    retrieveOrder;
  const {
    DiscountAmount,
    NetTotalAmount,
    SubTotalAmount,
    TaxAmount,
    TotalReturnAmount,
    ReturnAmount,
  } = Totals;

  const classes = useStyles();
  const { t } = useTranslation();

  const [refundTaxAmount, setRefundTaxAmount] = useState(0);

  useEffect(() => {
    Totals &&
      setRefundTaxAmount(refundTaxHandler(TotalReturnAmount, ReturnAmount));
  }, [Totals]);

  return retrieveOrder?.Items?.map((orderItem, index) => (
    <Box className={classes.orderList} key={index}>
      <List className={classes.orderListItem}>
        <OrderHistoryListHeader
          productThumbnailUrl={getThumbnailUrl(
            orderItem.Product,
            orderItem.PricingPlan,
            productMetadata,
          )}
          classname={classes}
          label={t(LocaleKeys.orderHistory.taxInvoice)}
          showInvoice={showInvoice}
        />

        <ListItemComponent
          classNameListItem={classes.orderListHeader}
          label={[
            {
              label: getLocaleLongDate(Completed || Ordered),
              variant: 'h5',
              classname: classes.orderListItemKey3,
              typographyClass: classes.titilliumSemiboldFont,
            },
            {
              label: `#${OrderNumber}`,
              variant: 'h5',
              classname: classes.orderInvoiceAndNumber,
              typographyClass: classes.titilliumSemiboldFont,
            },
          ]}
        />

        <Divider className={classes.dividerBlack} />

        <ListItemComponent
          classNameListItem={classes.invoiceAddressListItem}
          label={[
            {
              label: t(LocaleKeys.orderHistory.f1DigitalManagement),
              variant: 'h5',
              classname: classes.f1DigitalManagement,
              typographyClass: classes.titilliumSemiboldFont,
            },
            {
              label: t(LocaleKeys.orderHistory.f1Address),
              variant: 'body1',
              classname: classes.invoiceAddress,
            },
          ]}
        />

        <Divider />

        <ListItemComponent
          classNameListItem={classes.orderHistoryListItem}
          label={[
            {
              label: t(LocaleKeys.orderHistory.orderType),
              variant: 'body1',
              classname: classes.orderListItemKey1,
            },
            {
              label: orderItem.PricingPlan.Name,
              variant: 'h5',
              classname: classes.orderListItemText,
              typographyClass: classes.titilliumSemiboldFont,
            },
          ]}
        />

        <ListItemComponent
          classNameListItem={classes.orderHistoryListItem}
          label={[
            {
              label: t(LocaleKeys.orderHistory.type),
              variant: 'body1',
              classname: classes.orderListItemKey1,
            },
            {
              label: TypeName,
              variant: 'h5',
              classname: classes.orderListItemText,
              typographyClass: classes.titilliumSemiboldFont,
            },
          ]}
        />

        <ListItemComponent
          classNameListItem={classes.orderHistoryListItem}
          label={[
            {
              label: t(LocaleKeys.orderHistory.originalAmount),
              variant: 'body1',
              classname: classes.orderListItemKey1,
            },
            {
              label: getCurrencyDetails(orderItem.GrossAmount, Currency)
                .priceWithCurrencySymbol,
              variant: 'h5',
              classname: classes.orderListItemText,
              typographyClass: classes.titilliumSemiboldFont,
            },
          ]}
        />

        <ListItemComponent
          classNameListItem={`${classes.orderHistoryListItem} ${classes.orderHistoryListLastItem}`}
          label={[
            {
              label: t(LocaleKeys.orderHistory.discountApplied),
              variant: 'body1',
              classname: classes.orderListItemKey1,
            },
            {
              label: DiscountAmount
                ? `-${
                    getCurrencyDetails(DiscountAmount, Currency)
                      .priceWithCurrencySymbol
                  }`
                : getCurrencyDetails(DiscountAmount, Currency)
                    .priceWithCurrencySymbol,
              variant: 'h5',
              classname: classes.orderListItemText,
              typographyClass: classes.titilliumSemiboldFont,
            },
          ]}
        />

        <Divider className={classes.dividerBlack} />

        <ListItemComponent
          classNameListItem={classes.orderHistoryListItem}
          label={[
            {
              label: t(LocaleKeys.orderHistory.refundAmount),
              variant: 'body1',
              classname: classes.orderListItemKey1,
            },
            {
              label: getCurrencyDetails(TotalReturnAmount, Currency)
                .priceWithCurrencySymbol,
              variant: 'h5',
              classname: classes.orderListItemText,
              typographyClass: classes.titilliumSemiboldFont,
            },
          ]}
        />

        <ListItemComponent
          classNameListItem={classes.orderHistoryListItem}
          label={[
            {
              label: t(LocaleKeys.orderHistory.subTotalAmount),
              variant: 'body1',
              classname: classes.orderListItemKey1,
            },
            {
              label: getCurrencyDetails(SubTotalAmount, Currency)
                .priceWithCurrencySymbol,
              variant: 'h5',
              classname: classes.orderListItemText,
              typographyClass: classes.titilliumSemiboldFont,
            },
          ]}
        />

        {retrieveOrder?.TaxItems?.length &&
          retrieveOrder.TaxItems.map((TaxItem, index) => (
            <ListItemComponent
              key={index}
              classNameListItem={classes.taxItem}
              label={[
                {
                  label: TaxItem.Name,
                  variant: 'body1',
                  classname: classes.orderListItemKey2,
                },
                {
                  label: getCurrencyDetails(TaxItem.Amount, Currency)
                    .priceWithCurrencySymbol,
                  variant: 'h5',
                  classname: classes.orderListItemText,
                  typographyClass: classes.titilliumSemiboldFont,
                },
              ]}
            />
          ))}

        <ListItemComponent
          classNameListItem={classes.taxItem}
          label={[
            {
              label: t(LocaleKeys.orderHistory.taxRefund),
              variant: 'body1',
              classname: classes.orderListItemKey2,
            },
            {
              label: refundTaxAmount
                ? `-${
                    getCurrencyDetails(refundTaxAmount, Currency)
                      .priceWithCurrencySymbol
                  }`
                : getCurrencyDetails(refundTaxAmount, Currency)
                    .priceWithCurrencySymbol,
              variant: 'h5',
              classname: classes.orderListItemText,
              typographyClass: classes.titilliumSemiboldFont,
            },
          ]}
        />

        <ListItemComponent
          classNameListItem={`${classes.orderHistoryListItem} ${classes.orderHistoryListLastItem}`}
          label={[
            {
              label: t(LocaleKeys.orderHistory.tax),
              variant: 'body1',
              classname: classes.orderListItemKey1,
            },
            {
              label: getCurrencyDetails(TaxAmount, Currency)
                .priceWithCurrencySymbol,
              variant: 'h5',
              classname: classes.orderListItemText,
              typographyClass: classes.titilliumSemiboldFont,
            },
          ]}
        />

        <Divider className={classes.dividerBlack} />

        <ListItemComponent
          classNameListItem={classes.orderHistoryListItem}
          label={[
            {
              label: t(LocaleKeys.orderHistory.totalAmount),
              variant: 'h5',
              classname: classes.orderListItemKey1,
              typographyClass: classes.titilliumSemiboldFont,
            },
            {
              label: getCurrencyDetails(NetTotalAmount, Currency)
                .priceWithCurrencySymbol,
              variant: 'h2',
              classname: `${classes.orderListItemText} ${classes.orderListLastItemText}`,
              typographyClass: classes.totalAmount,
            },
          ]}
        />
      </List>
    </Box>
  ));
};

OrderHistoryInvoice.propTypes = {
  retrieveOrder: PropTypes.object.isRequired,
};

export default OrderHistoryInvoice;
